import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  Bell, BellOff, Mail, MessageCircle, Users, 
  ChevronLeft, Loader2, Check, Package, Smartphone
} from "lucide-react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../../contexts/AuthContext";
import { Section, Button } from "./PageComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailPreferencesPage() {
  const { user, checkAuth } = useAuth();
  const [preferences, setPreferences] = useState({
    connection_requests: true,
    match_alerts: true,
    new_messages: true,
    forwarding_updates: true
  });
  const [smsPreferences, setSmsPreferences] = useState({
    forwarding_updates: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    try {
      const [emailRes, smsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/email-preferences`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/auth/sms-preferences`, { withCredentials: true })
      ]);
      setPreferences(emailRes.data);
      setSmsPreferences(smsRes.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const newValue = !preferences[key];
    const newPreferences = { ...preferences, [key]: newValue };
    setPreferences(newPreferences);
    
    setSaving(true);
    setSaved(false);
    setError(null);

    try {
      await axios.put(`${BACKEND_URL}/api/auth/email-preferences`, { [key]: newValue }, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !newValue }));
    } finally {
      setSaving(false);
    }
  };

  const handleSmsToggle = async (key) => {
    const newValue = !smsPreferences[key];
    const newPreferences = { ...smsPreferences, [key]: newValue };
    setSmsPreferences(newPreferences);
    
    setSaving(true);
    setSaved(false);
    setError(null);

    try {
      await axios.put(`${BACKEND_URL}/api/auth/sms-preferences`, { [key]: newValue }, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      // Revert on error
      setSmsPreferences(prev => ({ ...prev, [key]: !newValue }));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Please sign in first</h2>
          <Button href="#login">Sign In</Button>
        </div>
      </Section>
    );
  }

  const preferenceItems = [
    {
      key: "connection_requests",
      icon: Users,
      title: "Connection Requests",
      description: "Get notified when someone wants to be your pen pal"
    },
    {
      key: "match_alerts",
      icon: Bell,
      title: "Match Alerts",
      description: "Get notified when someone accepts your connection request"
    },
    {
      key: "new_messages",
      icon: MessageCircle,
      title: "New Messages",
      description: "Get notified when you receive a new in-app message"
    },
    {
      key: "forwarding_updates",
      icon: Package,
      title: "Forwarding Updates",
      description: "Get notified when your letter is received, forwarded, or when a letter is on its way to you"
    },
    {
      key: "renewal_reminders",
      icon: Bell,
      title: "Renewal Reminders",
      description: "Get reminded before your subscriptions renew (7 days, 3 days, and 1 day before)"
    }
  ];

  return (
    <Section id="email-preferences">
      <div className="max-w-2xl mx-auto" data-testid="email-preferences-page">
        <a href="#penpal-profile" className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-8 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> Back to Profile
        </a>
        
        <div className="flex items-center gap-3 mb-8">
          <Mail size={32} className="text-lws-green" />
          <div>
            <h1 className="text-3xl font-serif text-lws-green">Email Preferences</h1>
            <p className="text-lws-taupe">Manage your notification settings</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 mb-6">
            {error}
          </div>
        )}

        {saved && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-lws-green/10 text-lws-green p-4 mb-6 flex items-center gap-2"
          >
            <Check size={18} />
            Preferences saved
          </motion.div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-lws-green" />
          </div>
        ) : (
          <>
            {/* Email Preferences */}
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-lws-ink mb-4 flex items-center gap-2">
                <Mail size={20} className="text-lws-green" />
                Email Notifications
              </h2>
              {preferenceItems.map((item) => (
                <div
                  key={item.key}
                  className="border border-lws-ink/10 p-6 flex items-center justify-between"
                >
                  <div className="flex items-start gap-4">
                    <item.icon size={24} className="text-lws-amber mt-1" />
                    <div>
                      <h3 className="font-medium">{item.title}</h3>
                      <p className="text-sm text-lws-taupe mt-1">{item.description}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleToggle(item.key)}
                    disabled={saving}
                    className={`relative w-14 h-8 rounded-full transition-colors ${
                      preferences[item.key] ? 'bg-lws-green' : 'bg-lws-taupe/30'
                    }`}
                    data-testid={`toggle-${item.key}`}
                  >
                    <span
                      className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                        preferences[item.key] ? 'left-7' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>

            {/* SMS Preferences */}
            <div className="space-y-4 mt-12">
              <h2 className="text-xl font-serif text-lws-ink mb-4 flex items-center gap-2">
                <Smartphone size={20} className="text-lws-green" />
                SMS Notifications
              </h2>
              <p className="text-sm text-lws-taupe mb-4">
                Receive text message updates about your letter forwarding. SMS is sent via GoHighLevel to the phone number on your account.
              </p>
              
              <div className="border border-lws-ink/10 p-6 flex items-center justify-between">
                <div className="flex items-start gap-4">
                  <Package size={24} className="text-lws-amber mt-1" />
                  <div>
                    <h3 className="font-medium">Forwarding SMS Updates</h3>
                    <p className="text-sm text-lws-taupe mt-1">
                      Get text messages when your letter is received, forwarded, or when a letter is on its way to you
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => handleSmsToggle("forwarding_updates")}
                  disabled={saving}
                  className={`relative w-14 h-8 rounded-full transition-colors ${
                    smsPreferences.forwarding_updates ? 'bg-lws-green' : 'bg-lws-taupe/30'
                  }`}
                  data-testid="toggle-sms-forwarding"
                >
                  <span
                    className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                      smsPreferences.forwarding_updates ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </>
        )}

        <div className="mt-8 p-6 bg-lws-taupe/5">
          <h3 className="font-medium mb-2">Email Address</h3>
          <p className="text-lws-taupe">{user.email}</p>
          <p className="text-xs text-lws-taupe/60 mt-2">
            Notifications are sent to this email address. All emails come from noreply@letterwriterssociety.com.au
          </p>
        </div>
      </div>
    </Section>
  );
}
